/**
 * WebPopupProvider - DOM-based toast notifications for errors/warnings
 *
 * No external dependencies. Creates a fixed container and shows
 * auto-dismissing toast messages for ERROR and WARN level logs.
 */

import type { WebLogProvider, WebLogEntry } from '../types';
import { LogLevel } from '../types';
import { isWebPopupEnabled } from '../popupPref';

const CONTAINER_ID = 'web-log-toast-container';
const AUTO_DISMISS_MS: Record<number, number> = {
  [LogLevel.ERROR]: 8000,
  [LogLevel.WARN]: 5000,
};

function getOrCreateContainer(): HTMLElement {
  let el = document.getElementById(CONTAINER_ID);
  if (el) return el;

  el = document.createElement('div');
  el.id = CONTAINER_ID;
  el.style.cssText = `
    position: fixed; top: 16px; right: 16px; z-index: 99999;
    display: flex; flex-direction: column; gap: 8px;
    pointer-events: none; max-width: 420px;
  `;
  document.body.appendChild(el);
  return el;
}

function createToast(entry: WebLogEntry): void {
  const container = getOrCreateContainer();

  const toast = document.createElement('div');
  const isError = entry.level === LogLevel.ERROR;
  const bg = isError ? '#fee2e2' : '#fef3c7';
  const border = isError ? '#fca5a5' : '#fcd34d';
  const textColor = isError ? '#991b1b' : '#92400e';

  toast.style.cssText = `
    background: ${bg}; border: 1px solid ${border}; color: ${textColor};
    padding: 10px 14px; border-radius: 8px; font-size: 13px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15); pointer-events: auto;
    cursor: pointer; transition: opacity 0.3s; opacity: 0;
    max-width: 100%; word-break: break-word;
  `;

  const label = isError ? 'Error' : 'Warning';
  toast.innerHTML = `<strong>${label}:</strong> ${escapeHtml(entry.message)}`;

  // Click to dismiss
  toast.addEventListener('click', () => removeToast(toast));

  container.appendChild(toast);

  // Fade in
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
  });

  // Auto-dismiss
  const delay = AUTO_DISMISS_MS[entry.level] || 5000;
  setTimeout(() => removeToast(toast), delay);
}

function removeToast(toast: HTMLElement): void {
  toast.style.opacity = '0';
  setTimeout(() => toast.remove(), 300);
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export class WebPopupProvider implements WebLogProvider {
  name = 'web-popup' as const;

  log(entry: WebLogEntry): void {
    // Only show toasts for error and warn
    if (entry.level > LogLevel.WARN) return;
    // Device-local opt-in (default off) — checked at log time so toggling the
    // Settings switch takes effect immediately, independent of remote config.
    if (!isWebPopupEnabled()) return;
    createToast(entry);
  }
}
